import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
	UntypedFormBuilder,
	UntypedFormGroup,
	Validators,
} from '@angular/forms';
import {
	DateAdapter,
	MAT_DATE_FORMATS,
	MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
	MAT_MOMENT_DATE_ADAPTER_OPTIONS,
	MomentDateAdapter,
} from '@angular/material-moment-adapter';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Store } from '@ngrx/store';

import { BeneficiaryInfoDetailsDto } from '../../../../../modules/beneficiaries/dtos/beneficiary-info-details';
import { BeneficiaryWalletDto } from '../../../../../modules/beneficiaries/dtos/beneficiary-wallet';
import { PT_BR_DATE_FORMATS } from '../../../../../utils/date-formats.utils';
import {
	editDependentBenefitsClear,
	editDependentBenefitsStart,
	fetchDependentBenefitsStart,
	restoreFormReset,
} from '../../../state/organizations.actions';
import {
	selectDependentBenefits,
	selectEditDependentBenefitsModalResult,
	selectRestoreForm,
} from '../../../state/organizations.selector';

@Component({
	providers: [
		{ provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
		{
			deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
			provide: DateAdapter,
			useClass: MomentDateAdapter,
		},
		{ provide: MAT_DATE_FORMATS, useValue: PT_BR_DATE_FORMATS },
	],
	selector: 'app-edit-dependent-benefits-modal',
	styles: [
		`
			.col-1 {
				width: 346px;
				min-width: 346px;
				&.dyn {
					min-width: 0;
				}
			}
			.col-2 {
				width: 708px;
				min-width: 708px;
				&.dyn {
					min-width: 0;
				}
			}
			.gray {
				color: #626262;
			}
			@media only screen and (max-width: 1024px) {
				.line {
					flex-direction: column;
					align-items: flex-start;
				}
				.col-1,
				.col-2 {
					width: 95%;
					min-width: 95%;
				}
			}
		`,
	],
	templateUrl: './edit-dependent-benefits-modal.component.html',
})
export class EditDependentBenefitsModalComponent implements OnInit, OnDestroy {
	dentalBenefits: BeneficiaryInfoDetailsDto[] = [
		{ id: 'NAO_APLICAVEL', name: 'Benefício não aplicável' },
	];

	form: UntypedFormGroup;
	healthBenefits: BeneficiaryInfoDetailsDto[] = [
		{ id: 'NAO_APLICAVEL', name: 'Benefício não aplicável' },
	];

	inputId: string = String(Math.random());

	loading = false;
	nonAplic: BeneficiaryInfoDetailsDto[] = [
		{ id: 'NAO_APLICAVEL', name: 'Benefício não aplicável' },
	];
	wallets!: any;

	private readonly unsubscribe$: Subject<void> = new Subject();

	constructor(
		private readonly fb: UntypedFormBuilder,
		private readonly store: Store,
		public dialogRef: MatDialogRef<EditDependentBenefitsModalComponent>,
		@Inject(MAT_DIALOG_DATA)
		public data: {
			beneficiaryId: string;
			dental: BeneficiaryWalletDto | null;
			dependentId: string;
			health: BeneficiaryWalletDto | null;
			orgId: string;
		},
	) {
		this.form = this.fb.group({
			dental_card: [
				data.health?.not_aplicable
					? { disabled: true, value: null }
					: data.dental?.wallet_number,
			],
			dental_plan: [
				data.dental?.not_aplicable ? 'NAO_APLICAVEL' : data.dental?.benefit_id,
				[Validators.required],
			],
			health_card: [
				data.health?.not_aplicable
					? { disabled: true, value: null }
					: data.health?.wallet_number,
			],
			health_plan: [
				data.health?.not_aplicable ? 'NAO_APLICAVEL' : data.health?.benefit_id,
				[Validators.required],
			],
		});
	}

	closeModal() {
		this.dialogRef.close();
	}

	ngOnDestroy(): void {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
		this.store.dispatch(restoreFormReset());
	}

	ngOnInit(): void {
		this.store
			.select(selectDependentBenefits)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe((db) => {
				if (db) {
					this.healthBenefits = this.nonAplic.concat(db.healths);
					this.dentalBenefits = this.nonAplic.concat(db.teeths);
					this.loading = false;
				}
			});
		this.store.dispatch(
			fetchDependentBenefitsStart({ id: this.data.beneficiaryId }),
		);
	}

	onContinue(): void {
		if (this.form.valid) {
			const form = this.form.value;
			const request = {
				walletHealthId:
					form.health_plan === 'NAO_APLICAVEL' ? null : form.health_plan,
				walletHealthNotAplicable: form.health_plan === 'NAO_APLICAVEL',
				walletHealthNumber:
					form.health_plan === 'NAO_APLICAVEL' ? '' : form.health_card,
				walletTeethId:
					form.dental_plan === 'NAO_APLICAVEL' ? null : form.dental_plan,
				walletTeethNotAplicable: form.dental_plan === 'NAO_APLICAVEL',
				walletTeethNumber:
					form.dental_plan === 'NAO_APLICAVEL' ? '' : form.dental_card,
			};
			this.store
				.select(selectRestoreForm)
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe((restore) => {
					if (restore) {
						this.updateValidation();
						this.loading = false;
					}
				});
			this.store
				.select(selectEditDependentBenefitsModalResult)
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe((success) => {
					if (success) {
						this.store.dispatch(editDependentBenefitsClear());
						this.dialogRef.close(true);
					}
				});
			this.loading = true;
			this.store.dispatch(
				editDependentBenefitsStart({ depId: this.data.dependentId, request }),
			);
		}
	}

	updateForm(type: 'dental' | 'health', plan: any): void {
		if (plan === 'NAO_APLICAVEL') {
			this.form.get(`${type}_card`)!.setValidators([]);
			this.form.get(`${type}_card`)!.setValue(null);
			this.form.get(`${type}_card`)!.disable();
		} else {
			this.form.get(`${type}_card`)!.setValidators([]);
			this.form.get(`${type}_card`)!.enable();
		}
		this.form.get(`${type}_card`)!.updateValueAndValidity({ onlySelf: true });
	}

	updateValidation(): void {
		for (const control in this.form.controls) {
			this.form.get(control)!.markAsTouched();
			this.form.get(control)!.updateValueAndValidity({ onlySelf: true });
		}
	}
}
